/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./GroupOptimizerBase'
], (GroupOptimizerBase) => {

	class FactsQueryCombiner  extends GroupOptimizerBase {
		constructor(queryService, dataSources) {
			super(queryService, dataSources);
			this.requests = [];
		}

		getId() {
			return 'FactsQueryCombiner';
		}

		/**
		 * @function FactsQueryCombiner#isApplicable
		 * @description checks if this optimizer is applicable for the passed request
		 * Checks that all items in the querySpec are facts (aggregated)
		 *
		 * @param {Object} requestParams request parameters
		 * @param {String} requestParams.sourceIdOrModule: The sourceId with which query is based on
		 * @param {Object} requestParams.querySpec  the v1 query spec
		 * @param {Object} requestParams.requestOptions additional request options
		 * @param {String} [requestParams.senderId] The unique string identifier of the the query sender
		 */
		isApplicable(requestParams){
			let { querySpec } = requestParams;
			if( !querySpec ) {
				return false;
			}
			if(! querySpec.dataItems || !querySpec.dataItems.length) {
				return false;
			}

			if (querySpec.type) {
				//Non-default data queries such as minmax and detail are not supported.
				return false;
			}

			const dataSource = this.dataSources && this.dataSources.getDataSource(requestParams.sourceIdOrModule);
			if (!dataSource) {
				return false;
			}

			//Don't try to optimize a request with any non-facts or
			//whose measures come from more than one query subject.
			//(which would only happen for tables or multi-measure charts).
			const firstDataItem = querySpec.dataItems[0];
			const firstTableId = (firstDataItem.aggregate && this._getTableId(dataSource, firstDataItem));
			return querySpec.dataItems.every(dataItem => {
				return dataItem.aggregate && (firstTableId === this._getTableId(dataSource, dataItem));
			});
		}

		getRequestGroupKey(request) {
			let requestParams = request.requestParams;
			const sourceId = requestParams.sourceId || requestParams.sourceIdOrModule;
			const firstDataItem = requestParams.querySpec.dataItems && requestParams.querySpec.dataItems[0];
			const dataSource = this.dataSources && this.dataSources.getDataSource(requestParams.sourceIdOrModule);
			const querySubject = this._getTableId(dataSource, firstDataItem);
			return sourceId + '_' + querySubject + '_' + (requestParams.querySpec.filters ? this._getCompareString(requestParams.querySpec.filters, []) : 'noFilters') + '_'+ JSON.stringify(requestParams.requestOptions)+ '_' + JSON.stringify(requestParams.senderId);
		}

		executeGroup(requestInfoList) {

			// start with the first querySpec
			const optimizedQuery = JSON.parse(JSON.stringify(requestInfoList[0].request.requestParams.querySpec));
			optimizedQuery.projections = [];
			optimizedQuery.dataItems = [];

			// Go over the queries and add the dataitems and project to the query spec;
			for (let i = 0 ; i < requestInfoList.length; i++){
				const querySpec = requestInfoList[i].request.requestParams.querySpec;
				let j;
				for (j = 0 ; j < querySpec.dataItems.length; j++){
					const dataItem = JSON.parse(JSON.stringify(querySpec.dataItems[j]));
					dataItem.id += '_o'+i;
					optimizedQuery.dataItems.push(dataItem);
				}

				for (j = 0 ; j < querySpec.projections.length; j++){
					let id = querySpec.projections[j];
					id += '_o'+ i;
					optimizedQuery.projections.push(id);
				}
			}

			// We know that if we got here, all the filters are the same.
			// The only filter that referes to dataItemIds is the post-aggregate range filter.
			// Since that filter includes the aggregation type in its spec, the range must be the same when the key is the same.
			// That means we can safely put _o0 on it.
			optimizedQuery.filters && optimizedQuery.filters.forEach((filter) => {
				if (filter.expression && filter.expression.dataItem) {
					filter.expression.dataItem += '_o0';
				}
			});

			this.queryService.runQuery({
				sourceIdOrModule: requestInfoList[0].request.requestParams.sourceIdOrModule,
				querySpec: optimizedQuery,
				requestOptions: requestInfoList[0].request.requestParams.requestOptions,
				sender: requestInfoList[0].request.requestParams.senderId
			}).then((response) => {

				const dataItemMapbyId = {};
				//We always get a single row response (or an empty response)
				const dataRow = response.data.data[0];
				const combinedResponseIsEmpty = !dataRow;
				response.data.dataItems.forEach((dataItem, index) => {
					dataItemMapbyId[dataItem.itemClass.id] = {
						dataItem: dataItem,
						value: dataRow && dataRow.pt && dataRow.pt[index]
					};
					dataItem.itemClass.id = this.restoreId(dataItem.itemClass.id);
				});

				//split the response for each request and resolve
				for (let i = 0 ; i < requestInfoList.length; i++){
					const responseInfo = requestInfoList[i];
					const data = {
						meta: response.data.meta,
						version: response.data.version,
						data:[{
							pt:[]
						}],
						dataItems: []
					};

					//restore the ids  - remove _o
					responseInfo.request.requestParams.querySpec.dataItems.forEach (requestDataItem => {
						const id  = requestDataItem.id + '_o' + i;
						requestDataItem.id = this.restoreId(requestDataItem.id);
						data.dataItems.push(dataItemMapbyId[id].dataItem);
						if (combinedResponseIsEmpty) {
							data.data = [];
						} else {
							data.data[0].pt.push(dataItemMapbyId[id].value);
						}
					});

					responseInfo.response.resolve( {
						ETag: response.ETag,
						requestTime: response.requestTime,
						data: data,
						zipiId: response.zipiId
					} );
				}
			}).catch((jqXHR) => {
				for (let i = 0 ; i < requestInfoList.length; i++){
					const responseInfo = requestInfoList[i];
					responseInfo.response.reject( jqXHR );
				}
			});
		}
	}

	return FactsQueryCombiner;
});
